import { formatHour } from "./utils";

/**
 * Smart Timeline — renders the day's schedule as a horizontal track with
 * meeting blocks, lunch / shutdown / rounds / focus-time fillers, and a
 * "now" marker. Routine block toggles (incidentReview / rounds / lunch /
 * shutdown) come from the morning setup; meetings come from the calendar.
 */
const SmartTimeline = ({ meetings, currentTime, workHours, routineBlocks }) => {
  const startHour = workHours?.start || 7.5;
  const endHour = workHours?.end || 16;
  const lunchDuration = workHours?.lunchDuration || 0.5;

  // Generate optimized schedule based on meetings and enabled routine blocks
  const generateSchedule = () => {
    const schedule = [];

    // Sort meetings by start time
    const sortedMeetings = [...meetings].sort(
      (a, b) => (a.start_time ?? a.start) - (b.start_time ?? b.start)
    );

    // Add meetings first (they take priority)
    sortedMeetings.forEach((meeting, idx) => {
      const meetingStart = meeting.start_time ?? meeting.start;
      const meetingEnd = meeting.end_time ?? meeting.end;
      if (meetingStart >= startHour && meetingEnd <= endHour) {
        schedule.push({
          start: meetingStart,
          end: meetingEnd,
          label: meeting.title || `Meeting ${idx + 1}`,
          type: "meeting",
          importance: meeting.importance,
        });
      }
    });

    // Calculate lunch time (middle of the day)
    const midDay = startHour + (endHour - startHour) / 2;
    const lunchStart = Math.floor(midDay * 2) / 2;

    // Add enabled routine blocks only
    if (routineBlocks?.lunch) {
      schedule.push({
        start: lunchStart,
        end: lunchStart + lunchDuration,
        label: `Lunch`,
        type: "break",
      });
    }

    if (routineBlocks?.shutdown) {
      const shutdownStart = endHour - 0.5;
      schedule.push({
        start: shutdownStart,
        end: endHour,
        label: "Shutdown",
        type: "admin",
      });
    }

    // Find gaps and intelligently fill them
    const allBlocks = [...schedule].sort((a, b) => a.start - b.start);
    const gaps = [];

    let lastEnd = startHour;
    allBlocks.forEach((block) => {
      if (block.start > lastEnd + 0.25) {
        gaps.push({ start: lastEnd, end: block.start });
      }
      lastEnd = Math.max(lastEnd, block.end);
    });

    if (lastEnd < endHour) {
      gaps.push({ start: lastEnd, end: endHour });
    }

    // Fill gaps based on enabled routines
    let incidentReviewDone = false;
    let roundsScheduled = 0;
    const maxRounds = routineBlocks?.rounds ? 2 : 0;

    gaps.forEach((gap) => {
      const gapDuration = gap.end - gap.start;
      let currentStart = gap.start;

      // Incident review at first available slot in morning
      if (routineBlocks?.incidentReview && !incidentReviewDone && currentStart < startHour + 2) {
        const reviewDuration = 0.5; // 30 min
        if (gapDuration >= reviewDuration) {
          schedule.push({
            start: currentStart,
            end: currentStart + reviewDuration,
            label: "Incident Review",
            type: "focus",
          });
          currentStart += reviewDuration;
          incidentReviewDone = true;
        }
      }

      // Schedule rounds if enabled
      if (roundsScheduled < maxRounds && gap.end - currentStart >= 0.42) {
        const roundDuration = 0.42;
        const isMorning = currentStart < midDay;
        schedule.push({
          start: currentStart,
          end: currentStart + roundDuration,
          label: isMorning ? "AM Rounds" : "PM Rounds",
          type: "rounds",
        });
        currentStart += roundDuration;
        roundsScheduled++;
      }

      // Fill remaining with focus time
      if (gap.end - currentStart >= 0.25) {
        schedule.push({
          start: currentStart,
          end: gap.end,
          label: "Focus Time",
          type: "focus",
        });
      }
    });

    return schedule.sort((a, b) => a.start - b.start);
  };

  const blocks = generateSchedule();
  const totalHours = endHour - startHour;
  const nowPosition = ((currentTime - startHour) / totalHours) * 100;

  return (
    <div className="timeline-track relative h-20">
      {blocks.map((block, idx) => {
        const left = ((block.start - startHour) / totalHours) * 100;
        const width = ((block.end - block.start) / totalHours) * 100;
        const importanceClass = block.importance === "high" ? "ring-2 ring-red-500" : "";
        return (
          <div
            key={idx}
            className={`timeline-block block-${block.type} ${importanceClass}`}
            style={{ left: `${left}%`, width: `${width}%` }}
            title={`${block.label} (${formatHour(block.start)} - ${formatHour(block.end)})`}
          >
            <span className="truncate px-1 text-xs">{block.label}</span>
          </div>
        );
      })}
      {nowPosition >= 0 && nowPosition <= 100 && (
        <div className="timeline-now-marker" style={{ left: `${nowPosition}%` }} />
      )}
    </div>
  );
};

export default SmartTimeline;
